import { useEffect, useRef } from 'react';
import { getAPIUrls } from '../util/env';

type OrderStatusUpdate = {
  orderId: string;
  orderStatus: string;
  orderStatusUpdatedAt: string;
};

type SSECallbacks = {
  onEntityNameChange?: (updatedName: string) => void;
  onOrderStatusChange?: (updatedOrder: OrderStatusUpdate) => void;
};

let globalEventSource: EventSource | null = null;
let globalCallbacks: SSECallbacks[] = [];
const channel = new BroadcastChannel('sse_updates');

const useUnifiedSSE = (entityUUID: string | null, callbacks: SSECallbacks) => {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  useEffect(() => {
    if (!entityUUID) return;

    globalCallbacks.push(callbacksRef.current);

    const API_URL: string = getAPIUrls(
      '/notification-service/api/v1/subscribe'
    );
    const sseUrl = `${API_URL}/${entityUUID}`;

    const connect = () => {
      if (globalEventSource) {
        globalEventSource.close();
      }
      const es = new EventSource(sseUrl);
      globalEventSource = es;

      es.onopen = () => {
        // console.log('SSE connection opened');
      };

      es.onerror = (err: Event) => {
        const e = err as Event & {
          status?: number;
          target?: { readyState?: number };
        };
        if (
          e.target?.readyState === EventSource.CLOSED ||
          e.status === 200 ||
          e.status === 504
        ) {
          connect();
        }
      };

      es.addEventListener('ENTITY_NAME_UPDATED', (event: MessageEvent) => {
        const updatedName = event.data as string;
        globalCallbacks.forEach((cb) => cb.onEntityNameChange?.(updatedName));
        channel.postMessage({ type: 'ENTITY_NAME_UPDATED', data: updatedName });
      });

      es.addEventListener('ORDER_STATUS_UPDATED', (event: MessageEvent) => {
        try {
          const parsed: OrderStatusUpdate = JSON.parse(event.data);
          globalCallbacks.forEach((cb) => cb.onOrderStatusChange?.(parsed));
          channel.postMessage({ type: 'ORDER_STATUS_UPDATED', data: parsed });
        } catch (e) {
          console.error('Failed to parse ORDER_STATUS_UPDATED:', e);
        }
      });
    };

    if (!globalEventSource) {
      connect();
    }

    const bcHandler = (msg: MessageEvent) => {
      if (msg.data.type === 'ENTITY_NAME_UPDATED') {
        callbacksRef.current.onEntityNameChange?.(msg.data.data);
      }
      if (msg.data.type === 'ORDER_STATUS_UPDATED') {
        callbacksRef.current.onOrderStatusChange?.(msg.data.data);
      }
    };
    channel.addEventListener('message', bcHandler);

    return () => {
      globalCallbacks = globalCallbacks.filter(
        (cb) => cb !== callbacksRef.current
      );
      channel.removeEventListener('message', bcHandler);

      if (globalCallbacks.length === 0 && globalEventSource) {
        globalEventSource.close();
        globalEventSource = null;
      }
    };
  }, [entityUUID]);
};

export default useUnifiedSSE;
